import type { BuiltForm, FormField, FormPage } from '../models/built-form';

export const DEFAULT_PAGE_ID = 'page_1';
export const DEFAULT_PRIMARY_LABEL = 'Submit';
export const DEFAULT_NEXT_LABEL = 'Next';

export function createDefaultPage(overrides?: Partial<FormPage>): FormPage {
  return {
    id: overrides?.id ?? DEFAULT_PAGE_ID,
    order: overrides?.order ?? 1,
    ...(overrides?.title ? { title: overrides.title } : {}),
    ...(overrides?.description ? { description: overrides.description } : {}),
    cta: {
      primaryLabel: overrides?.cta?.primaryLabel ?? DEFAULT_PRIMARY_LABEL,
      ...(overrides?.cta?.backLabel
        ? { backLabel: overrides.cta.backLabel }
        : {}),
    },
    fields: overrides?.fields ?? [],
  };
}

export function sortedPages(pages: FormPage[] | undefined): FormPage[] {
  return [...(pages ?? [])].sort((a, b) => a.order - b.order);
}

export function findPage(
  form: BuiltForm | null | undefined,
  pageId: string | null | undefined,
): FormPage | null {
  if (!form?.pages?.length) {
    return null;
  }
  const pages = sortedPages(form.pages);
  if (!pageId) {
    return pages[0] ?? null;
  }
  return pages.find((page) => page.id === pageId) ?? pages[0] ?? null;
}

export function allFormFields(form: BuiltForm | null | undefined): FormField[] {
  if (!form?.pages?.length) {
    return [];
  }
  return sortedPages(form.pages).flatMap((page) => page.fields ?? []);
}

export function allFieldIds(form: BuiltForm | null | undefined): Set<string> {
  return new Set(allFormFields(form).map((field) => field.id));
}

export function nextPageId(pages: FormPage[]): string {
  let max = 0;
  for (const page of pages) {
    const match = /^page_(\d+)$/.exec(page.id);
    if (match) {
      max = Math.max(max, Number(match[1]));
    }
  }
  return `page_${max + 1}`;
}

export function reindexPageOrders(pages: FormPage[]): FormPage[] {
  return sortedPages(pages).map((page, index) => ({
    ...page,
    order: index + 1,
  }));
}

/** Reindex by array position (for drag-reorder) and normalize back CTAs. */
export function applyPageSequence(pages: FormPage[]): FormPage[] {
  return pages.map((page, index) => {
    const order = index + 1;
    const primaryLabel = page.cta?.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL;
    const backLabel =
      order === 1
        ? undefined
        : page.cta?.backLabel?.trim() || 'Back';
    return {
      ...page,
      order,
      cta: {
        primaryLabel,
        ...(backLabel ? { backLabel } : {}),
      },
    };
  });
}

export function pageDisplayLabel(page: FormPage, index: number): string {
  const title = page.title?.trim();
  return title || `Page ${index + 1}`;
}

export function updatePageFields(
  form: BuiltForm,
  pageId: string,
  fields: FormField[],
): BuiltForm {
  return {
    ...form,
    pages: form.pages.map((page) =>
      page.id === pageId ? { ...page, fields } : page,
    ),
  };
}

export function mapPages(
  form: BuiltForm,
  mapper: (page: FormPage) => FormPage,
): BuiltForm {
  return {
    ...form,
    pages: form.pages.map(mapper),
  };
}

export function ensurePages(form: BuiltForm): BuiltForm {
  if (form.pages?.length) {
    return { ...form, pages: reindexPageOrders(form.pages) };
  }
  return {
    ...form,
    pages: [createDefaultPage()],
  };
}
